import { useEffect, useState } from 'react';
import axios from 'axios';
import { ClockCounterClockwise, SortAscending, SortDescending } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const actionColors = {
  LOGIN: 'bg-blue-50 text-blue-700 border-blue-200',
  VOLUNTEER_LOGIN: 'bg-sky-50 text-sky-700 border-sky-200',
  REGISTER: 'bg-green-50 text-green-700 border-green-200',
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  DELETE_VOLUNTEER_DOCUMENT: 'bg-red-50 text-red-700 border-red-200',
  ASSIGN_VOLUNTEER: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  INCLUDE_VOLUNTEER: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  MARK_APTO: 'bg-teal-50 text-teal-700 border-teal-200',
  MARK_NO_APTO: 'bg-red-50 text-red-700 border-red-200',
  EXCLUDE_VOLUNTEER: 'bg-red-50 text-red-700 border-red-200',
  UPDATE_VOLUNTEER_STATUS: 'bg-orange-50 text-orange-700 border-orange-200',
};

const moduleColors = {
  auth: 'text-blue-600', volunteers: 'text-green-600', studies: 'text-purple-600',
  appointments: 'text-orange-600', study_volunteers: 'text-indigo-600',
};

/**
 * Historial de auditoria completo de un voluntario a traves de todos los
 * modulos (datos, citas, inclusiones, consentimientos, dispensaciones,
 * cuestionarios, etc.). Reutiliza GET /api/audit/volunteers/{id}/timeline.
 * Requiere rol admin en backend.
 */
export default function VolunteerAuditTimeline({ volunteerId, open, onClose, lang = 'es' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    if (!open || !volunteerId) return;
    setLoading(true);
    setError(null);
    setData({ volunteer_id: volunteerId, volunteer: null, events: [] });
    axios.get(`${API_URL}/api/audit/volunteers/${volunteerId}/timeline`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(err => {
        if (err?.response?.status === 403) {
          setError(lang === 'es' ? 'Solo los administradores pueden ver el historial de auditoria.' : 'Only admins can view the audit timeline.');
        } else {
          setError(lang === 'es' ? 'No se pudo cargar el historial.' : 'Could not load the timeline.');
        }
        setData({ volunteer_id: volunteerId, volunteer: null, events: [] });
      })
      .finally(() => setLoading(false));
  }, [open, volunteerId, lang]);

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  // Humaniza una clave: fecha_nacimiento -> "Fecha nacimiento"
  const humanizeKey = (k) => {
    const s = String(k).replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const formatVal = (v) => {
    if (v === undefined || v === null || v === '') return '—';
    if (typeof v === 'boolean') return v ? (lang === 'es' ? 'Si' : 'Yes') : 'No';
    if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') : '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  // Diferencia campo a campo entre dos objetos (solo claves modificadas).
  const diffObject = (oldObj, newObj) => {
    const o = oldObj || {}; const n = newObj || {};
    const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));
    return keys
      .filter(k => k !== 'updated_at' && !eq(o[k], n[k]))
      .map(k => ({ key: k, before: o[k], after: n[k] }));
  };

  // Epigrafes agrupados para UPDATE de voluntarios (solo lo modificado).
  const SECTION_LABELS = {
    personal_data: lang === 'es' ? 'Datos personales' : 'Personal data',
    health_questionnaire: lang === 'es' ? 'Q Salud' : 'Health Q',
    documents: lang === 'es' ? 'Documentos' : 'Documents',
    status: lang === 'es' ? 'Estado' : 'Status',
    display_name: lang === 'es' ? 'Nombre mostrado' : 'Display name',
    qsalud_last_reviewed_at: lang === 'es' ? 'Revision Q-Salud (fecha)' : 'Q-Salud review (date)',
    qsalud_last_reviewed_by: lang === 'es' ? 'Revision Q-Salud (revisor)' : 'Q-Salud review (reviewer)',
  };

  const buildVolunteerGroups = (ev) => {
    const ov = ev.old_values || {};
    const nv = ev.new_values || {};
    const keys = Array.from(new Set([...Object.keys(ov), ...Object.keys(nv)])).filter(k => k !== 'updated_at');
    const groups = [];
    for (const k of keys) {
      if (k === 'personal_data' || k === 'health_questionnaire' || k === 'documents') {
        const changes = diffObject(ov[k], nv[k]);
        if (changes.length) groups.push({ section: SECTION_LABELS[k] || humanizeKey(k), changes });
      } else if (!eq(ov[k], nv[k])) {
        // status, display_name u otros campos sueltos
        groups.push({ section: SECTION_LABELS[k] || humanizeKey(k), changes: [{ key: null, before: ov[k], after: nv[k] }] });
      }
    }
    return groups;
  };

  // Etiqueta amigable segun categoria (solo acciones de la lista blanca).
  const friendlyAction = (ev) => {
    const es = lang === 'es';
    if (ev.action === 'VOLUNTEER_LOGIN') return es ? 'Inicio de sesion (app)' : 'App login';
    if (ev.module === 'appointments') {
      if (ev.action === 'CREATE') return es ? 'Cita creada' : 'Appointment created';
      if (ev.action === 'UPDATE') return es ? 'Cita modificada' : 'Appointment updated';
      if (ev.action === 'DELETE') return es ? 'Cita eliminada' : 'Appointment deleted';
    }
    if (ev.module === 'volunteers') {
      if (ev.action === 'CREATE') return es ? 'Voluntario creado' : 'Volunteer created';
      if (ev.action === 'UPDATE') return es ? 'Datos del voluntario' : 'Volunteer data';
      if (ev.action === 'DELETE') return es ? 'Voluntario eliminado' : 'Volunteer deleted';
    }
    if (ev.action === 'INCLUDE_VOLUNTEER') return es ? 'Incluido en estudio' : 'Included in study';
    if (ev.action === 'ASSIGN_VOLUNTEER') return es ? 'Asignado a estudio' : 'Assigned to study';
    if (ev.action === 'MARK_APTO') return es ? 'Marcado como apto' : 'Marked as eligible';
    if (ev.action === 'MARK_NO_APTO') return es ? 'Marcado como no apto' : 'Marked as not eligible';
    if (ev.action === 'EXCLUDE_VOLUNTEER') return es ? 'Excluido del estudio' : 'Excluded from study';
    if (ev.action === 'UPDATE_VOLUNTEER_STATUS') {
      const st = (ev.details || {}).new_status;
      const map = { abandono: es ? 'Abandono del estudio' : 'Study dropout', excluido: es ? 'Excluido del estudio' : 'Excluded from study', no_apto: es ? 'Marcado como no apto' : 'Marked as not eligible', retirado: es ? 'Retirado del estudio' : 'Withdrawn from study', completado: es ? 'Estudio completado' : 'Study completed', incluido: es ? 'Incluido en estudio' : 'Included in study' };
      return map[st] || (es ? 'Cambio de estado en estudio' : 'Study status change');
    }
    if (ev.action === 'DELETE_VOLUNTEER_DOCUMENT') return es ? 'Documento eliminado' : 'Document deleted';
    if (ev.action === 'QSALUD_REVIEW') return es ? 'Revision Q-Salud' : 'Q-Salud review';
    return ev.action;
  };

  // Linea de contexto: "Estudio EST-0622 · Visita T7D · 2026-10-22".
  const eventContext = (ev) => {
    const es = lang === 'es';
    const d = ev.details || {};
    const parts = [];
    let study = d.study_id || d.study;
    if (!study && typeof ev.resource_id === 'string' && ev.resource_id.startsWith('EST-')) study = ev.resource_id;
    if (study) parts.push(`${es ? 'Estudio' : 'Study'} ${study}`);
    if (d.visit_code) parts.push(`${es ? 'Visita' : 'Visit'} ${d.visit_code}`);
    if (d.code) parts.push(`${es ? 'Codigo' : 'Code'} ${d.code}`);
    if (ev.module === 'appointments' && d.date) parts.push(d.date);
    if ((d.notes || '').trim()) parts.push(`${es ? 'Motivo' : 'Reason'}: ${d.notes.trim()}`);
    return parts.join(' · ');
  };

  // Cambios planos (p.ej. cita modificada: fecha/hora/estado), excluyendo claves de contexto.
  const APPT_CONTEXT_KEYS = new Set(['updated_at', 'study_id', 'study', 'volunteer', 'volunteer_id', 'visit_code']);
  const flatChanges = (ev) => diffObject(ev.old_values, ev.new_values).filter(c => !APPT_CONTEXT_KEYS.has(c.key));

  // Lista blanca: solo lo que pertenece a las pestanas de la ficha del
  // voluntario (datos, Q-Salud, documentos, citas, inclusion en estudio y
  // login de la app). Los cuestionarios de estudio, consentimientos, firmas,
  // dispensaciones y encuestas quedan fuera (iran en la auditoria por estudio).
  const ALLOWED_ACTIONS = new Set(['VOLUNTEER_LOGIN', 'INCLUDE_VOLUNTEER', 'ASSIGN_VOLUNTEER', 'MARK_APTO', 'MARK_NO_APTO', 'EXCLUDE_VOLUNTEER', 'UPDATE_VOLUNTEER_STATUS', 'DELETE_VOLUNTEER_DOCUMENT', 'QSALUD_REVIEW']);
  const isAllowedEvent = (ev) => {
    if (ALLOWED_ACTIONS.has(ev.action)) return true;
    if (ev.module === 'volunteers' && ['CREATE', 'UPDATE', 'DELETE'].includes(ev.action)) return true;
    if (ev.module === 'appointments' && ['CREATE', 'UPDATE', 'DELETE'].includes(ev.action)) return true;
    return false;
  };

  const events = (data?.events || []).filter(isAllowedEvent);
  const ordered = order === 'desc' ? events : [...events].reverse();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="vol-audit-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-[#1f3e80]" />
            {lang === 'es' ? 'Historial de auditoria del voluntario' : 'Volunteer audit timeline'}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{volunteerId}</DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-xs text-amber-600 italic" data-testid="vol-audit-error">{error}</p>
        )}

        {data?.volunteer && (
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs grid grid-cols-2 gap-2" data-testid="vol-audit-header">
            <div><span className="text-stone-400">{lang === 'es' ? 'Voluntario' : 'Volunteer'}: </span><span className="font-mono">{data.volunteer.volunteer_id}</span></div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Nombre' : 'Name'}: </span>{data.volunteer.name || '-'}</div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Estado' : 'Status'}: </span>{data.volunteer.status || '-'}</div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
        ) : ordered.length === 0 && !error ? (
          <p className="text-sm text-stone-400 italic">{lang === 'es' ? 'Sin eventos de auditoria para este voluntario.' : 'No audit events for this volunteer.'}</p>
        ) : ordered.length > 0 ? (
          <>
            <div className="flex items-center justify-between gap-2 min-w-0" data-testid="vol-audit-toolbar">
              <p className="text-[11px] text-stone-500 font-mono shrink-0">{events.length} {lang === 'es' ? 'registros' : 'records'}</p>
              <Button variant="outline" size="sm" data-testid="vol-audit-order-toggle"
                onClick={() => setOrder(o => (o === 'desc' ? 'asc' : 'desc'))}
                className="rounded-sm gap-1 h-7 text-xs text-stone-600 shrink-0 whitespace-nowrap">
                {order === 'desc' ? <SortDescending size={14} /> : <SortAscending size={14} />}
                {order === 'desc'
                  ? (lang === 'es' ? 'Reciente primero' : 'Newest first')
                  : (lang === 'es' ? 'Antiguo primero' : 'Oldest first')}
              </Button>
            </div>
            <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4 py-1 min-w-0" data-testid="vol-audit-events">
              {ordered.map((ev, i) => (
                <li key={ev.id || i} className="ml-4 relative min-w-0" data-testid={`vol-audit-event-${i}`}>
                  <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1f3e80] border-2 border-white"></span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[ev.action] || 'bg-stone-50 text-stone-600'}`}>{friendlyAction(ev)}</Badge>
                    <span className="text-xs font-mono text-stone-500">{formatDate(ev.timestamp)}</span>
                    <span className="text-xs text-stone-500">· {ev.user_email || '-'}</span>
                  </div>
                  {eventContext(ev) && (
                    <p className="text-[11px] text-stone-500 mt-0.5 break-words">{eventContext(ev)}</p>
                  )}
                  {ev.action === 'UPDATE' && ev.module === 'volunteers' ? (
                    (() => {
                      const groups = buildVolunteerGroups(ev);
                      if (!groups.length) {
                        return <p className="text-xs text-stone-400 italic mt-1">{lang === 'es' ? 'Sin cambios de valor' : 'No value changes'}</p>;
                      }
                      return (
                        <div className="mt-1.5 space-y-2" data-testid={`vol-audit-groups-${i}`}>
                          {groups.map((g, gi) => (
                            <div key={gi} className="min-w-0">
                              <p className="text-[11px] font-semibold text-[#1f3e80] uppercase tracking-wide">{g.section}</p>
                              <div className="mt-0.5 space-y-0.5 pl-2 border-l border-stone-200">
                                {g.changes.map((c, ci) => (
                                  <div key={ci} className="text-[11px] text-stone-600 flex flex-wrap items-baseline gap-1 min-w-0">
                                    {c.key && <span className="font-medium text-stone-500">{humanizeKey(c.key)}:</span>}
                                    <span className="font-mono text-stone-400 line-through break-all">{formatVal(c.before)}</span>
                                    <span className="text-stone-400">→</span>
                                    <span className="font-mono text-stone-700 break-all">{formatVal(c.after)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : ev.action === 'UPDATE' && ev.module === 'appointments' ? (
                    (() => {
                      const changes = flatChanges(ev);
                      if (!changes.length) return null;
                      return (
                        <div className="mt-1 space-y-0.5 pl-2 border-l border-stone-200" data-testid={`vol-audit-appt-changes-${i}`}>
                          {changes.map((c, ci) => (
                            <div key={ci} className="text-[11px] text-stone-600 flex flex-wrap items-baseline gap-1 min-w-0">
                              <span className="font-medium text-stone-500">{humanizeKey(c.key)}:</span>
                              <span className="font-mono text-stone-400 line-through break-all">{formatVal(c.before)}</span>
                              <span className="text-stone-400">→</span>
                              <span className="font-mono text-stone-700 break-all">{formatVal(c.after)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : null}
                </li>
              ))}
            </ol>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
